import { Token } from '@/client/auth';
import { TokenResponse } from '@/client/auth/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export const authService = (client: AxiosInstance) => {
  const getLoginURL = async (username: string): Promise<{ url: string }> => {
    try {
      const response = await client.get<
        TokenResponse,
        AxiosResponse<{ url: string }>
      >('/locate', {
        params: {
          username,
        },
      });

      return response.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const handshakeToken = async (token: string): Promise<Token | undefined> => {
    try {
      const result = await client.post<
        TokenResponse,
        AxiosResponse<TokenResponse>
      >('/auth/handshake', null, {
        headers: {
          'x-set-cookie': true,
          Authorization: `Bearer ${token}`,
        },
      });

      return new Token(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const signOut = async (token?: string): Promise<boolean | undefined> => {
    const headers: Record<string, string | boolean> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      await client.get<TokenResponse, AxiosResponse<TokenResponse>>(
        '/auth/sign-out',
        {
          headers,
        },
      );

      return true;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const refreshToken = async (token?: string): Promise<Token | undefined> => {
    const headers: Record<string, string | boolean> = {
      'x-set-cookie': true,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const result = await client.post<
        TokenResponse,
        AxiosResponse<TokenResponse>
      >('/auth/refresh-token', null, {
        headers,
      });

      return new Token(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const forgotPassword = async (username: string): Promise<void> => {
    await client.post('/locate/forgot-password', { username });
  };

  return { handshakeToken, refreshToken, signOut, getLoginURL, forgotPassword };
};
